import { useEffect, useRef, useState } from 'react'
import { Image, Trash2, Upload, Video, X } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { PageTitle } from '../../components/ui/PageTitle'
import { StatusPill } from '../../components/ui/StatusPill'
import { api } from '../../lib/api'

type GalleryItem = {
  id: number
  title: string
  url: string
  filename: string
  mimetype: string
  type: 'image' | 'video'
  status: 'Active' | 'Inactive'
}

type UploadResponse = { url: string; filename: string; mimetype: string }

export function GalleryPage() {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [items, setItems] = useState<GalleryItem[]>([])
  const [uploading, setUploading] = useState(false)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const load = async () => {
    const { data } = await api.get('/admin/gallery')
    setItems(data.media ?? [])
  }

  useEffect(() => { void load() }, [])

  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length) return
    setUploading(true)
    try {
      const formData = new FormData()
      Array.from(files).forEach(file => formData.append('files', file))
      const { data } = await api.post('/upload/multiple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      for (const file of data.files as UploadResponse[]) {
        await api.post('/admin/gallery', {
          title: file.filename,
          filename: file.filename,
          url: file.url,
          mimetype: file.mimetype,
          type: file.mimetype.startsWith('video/') ? 'video' : 'image',
        })
      }
      await load()
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const updateItem = async (item: GalleryItem, patch: Partial<GalleryItem>) => {
    const next = { ...item, ...patch }
    setSavingId(item.id)
    try {
      await api.put(`/admin/gallery/${item.id}`, {
        title: next.title,
        status: next.status,
      })
      setItems(current => current.map(row => row.id === item.id ? next : row))
    } finally {
      setSavingId(null)
    }
  }

  const removeItem = async () => {
    if (!deleteId) return
    await api.delete(`/admin/gallery/${deleteId}`)
    setItems(current => current.filter(item => item.id !== deleteId))
    setDeleteId(null)
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Gallery"
        subtitle="Manage public and customer gallery images and videos."
        action={
          <label className="inline-flex min-h-10 cursor-pointer items-center justify-center gap-2 rounded-lg bg-mango-orange px-4 text-sm font-semibold text-white shadow-sm hover:bg-[#d98509]">
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading...' : 'Upload media'}
            <input ref={fileInputRef} className="sr-only" type="file" accept="image/*,video/*" multiple disabled={uploading} onChange={(event) => uploadFiles(event.target.files)} />
          </label>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.length === 0 ? (
          <div className="rounded-xl border border-stone-200 bg-white p-8 text-sm font-medium text-stone-500 sm:col-span-2 lg:col-span-3 xl:col-span-4">
            No gallery media yet. Upload images or videos to start.
          </div>
        ) : items.map(item => (
          <article key={item.id} className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <div className="relative aspect-[4/3] bg-stone-100">
              {item.type === 'video' ? (
                <video src={item.url} className="size-full object-cover" controls />
              ) : (
                <img src={item.url} alt={item.title || 'Gallery media'} className="size-full object-cover" />
              )}
              <span className="absolute left-3 top-3 grid h-9 w-9 place-items-center rounded-lg bg-white/90 text-leaf-dark shadow-sm">
                {item.type === 'video' ? <Video className="h-4 w-4" /> : <Image className="h-4 w-4" />}
              </span>
            </div>
            <div className="space-y-3 p-4">
              <div className="flex items-center justify-between gap-2">
                <StatusPill value={item.status} />
                <button className="rounded-lg p-2 text-red-500 hover:bg-red-50" onClick={() => setDeleteId(item.id)} title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <input
                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-mango-orange"
                value={item.title}
                placeholder="Media title"
                onChange={(event) => setItems(current => current.map(row => row.id === item.id ? { ...row, title: event.target.value } : row))}
                onBlur={(event) => updateItem(item, { title: event.target.value })}
              />
              <select
                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-mango-orange"
                value={item.status}
                onChange={(event) => updateItem(item, { status: event.target.value as GalleryItem['status'] })}
                disabled={savingId === item.id}
              >
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>
          </article>
        ))}
      </section>

      {deleteId !== null && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-stone-950/40 p-4">
          <div className="w-full max-w-sm rounded-xl border border-stone-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-stone-950">Delete media?</h3>
                <p className="mt-1 text-sm leading-6 text-stone-600">This removes the item from the gallery.</p>
              </div>
              <button className="rounded-lg p-1 text-stone-400 hover:bg-stone-100" onClick={() => setDeleteId(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setDeleteId(null)} variant="ghost">Cancel</Button>
              <Button onClick={removeItem} variant="danger">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
